/**
 * 逐镜参考 → 「看得见的素材 + 说人话的锁定说明」(批 B1,2026-08-05)。**纯解析,无 IO**。
 *
 * 为什么要有这一层:R2V 的选人/选品/配动作今天在镜卡上只剩一行「人物图×2 + 动作×1」和一个
 * 写着文件名的 chip —— 用户根本看不见锁的是**谁**。而素材的图/视频 URL 全是现成的
 * (动作库列表带 thumbUrl、人物 materials 带 thumbUrl、产品图有 presign),缺的只是把它们
 * 接到镜卡上。取数在 `useReferenceThumbs`,这里只做认领与措辞,好单测。
 *
 * ★assetRef 有**两种形态**(ai 侧 `reference_compiler` 认全的那两种,这里必须同样认全):
 *   ① 裸 objectKey(console 选人/选动作写的);② 实体 id `C1/P2/EP/LIBC7`(导演写的)。
 *   只认一种 = 另一种永远显示不出图,还看不出是哪一种坏了。
 */
import type { VideoShotReference } from "../api/videoService";

export interface RefCatalog {
  /** 动作库:objectKey → 库名/分类/封面/可播视频(videoAssetService.listMotions 带回)。 */
  motions?: Record<
    string,
    { name: string; category?: string; thumbUrl?: string | null; videoUrl?: string | null }
  >;
  /** 选中人物的物料:objectKey → 标签/缩略图(characterService 详情带回)。 */
  characterMaterials?: Record<string, { label?: string | null; thumbUrl?: string | null }>;
  /** 实体表:id → 名字/key/角色(form.entities)。 */
  entities?: Record<string, { name?: string | null; key?: string | null; role?: string | null }>;
  /** key → 现签 URL(产品图 presign 的结果)。 */
  presigned?: Record<string, string>;
  /** 选中人物的名字(人物物料没有各自的名字,统一用人物名)。 */
  characterName?: string | null;
}

export interface ResolvedRef {
  kind: "identity" | "product" | "motion" | "other";
  /** 展示名:库名 > 实体名 > job 里的「」名 > 文件名。**认不出就退,绝不编。** */
  name: string;
  thumbUrl?: string;
  /** 动作素材可播(hover 播 2 秒比一张封面有用得多)。 */
  videoUrl?: string;
  assetRef: string;
}

/** job 派活句里的「素材名」——ai 侧 `normalize_motion_jobs` 保证动作类一定带,其余尽力。 */
function nameFromJob(job?: string | null): string | null {
  const m = /「(.+?)」/.exec(job ?? "");
  return m ? m[1] : null;
}

function fileName(assetRef: string): string {
  return assetRef.split("/").pop() || assetRef;
}

export function resolveShotReferences(
  refs: VideoShotReference[] | null | undefined,
  catalog: RefCatalog
): ResolvedRef[] {
  const motions = catalog.motions ?? {};
  const materials = catalog.characterMaterials ?? {};
  const entities = catalog.entities ?? {};
  const presigned = catalog.presigned ?? {};

  return (refs ?? []).map((r) => {
    const assetRef = String(r?.assetRef ?? "");
    const role = (r?.role ?? "other") as ResolvedRef["kind"];
    // ① 动作库直接命中 objectKey
    const motion = motions[assetRef];
    if (motion) {
      return {
        kind: "motion",
        name: motion.name,
        thumbUrl: motion.thumbUrl ?? undefined,
        videoUrl: motion.videoUrl ?? undefined,
        assetRef,
      };
    }
    // ② 人物物料按 objectKey 命中(选人路写裸 key)
    const material = materials[assetRef];
    if (material) {
      return {
        kind: "identity",
        name: catalog.characterName || material.label || fileName(assetRef),
        thumbUrl: material.thumbUrl ?? undefined,
        assetRef,
      };
    }
    // ③ 实体 id 命中(导演路写 C1/P2/EP)
    const entity = entities[assetRef];
    if (entity) {
      const url = entity.key ? presigned[entity.key] : undefined;
      return {
        kind: role,
        name: entity.name || nameFromJob(r?.job) || assetRef,
        thumbUrl: url,
        assetRef,
      };
    }
    // ④ 认不出:退 job 名 → 文件名。**不编图,也不编名。**
    return { kind: role, name: nameFromJob(r?.job) || fileName(assetRef), assetRef };
  });
}

const KIND_PHRASE: Record<string, string> = {
  identity: "长相",
  product: "外观",
  motion: "动作节奏",
};

/**
 * 一句人话:「这镜锁定:长相=林小柔(2 张) / 外观=云杉CS-300 / 动作节奏=正面口播·站姿」。
 * 空参考返 `null` —— 概览不许比实际乐观,没有就别显示这行。
 */
export function describeShotReferences(items: ResolvedRef[]): string | null {
  if (!items.length) return null;
  const groups = new Map<string, { names: Set<string>; count: number }>();
  for (const it of items) {
    const phrase = KIND_PHRASE[it.kind] ?? "参考";
    const g = groups.get(phrase) ?? { names: new Set<string>(), count: 0 };
    g.names.add(it.name);
    g.count += 1;
    groups.set(phrase, g);
  }
  const parts = [...groups.entries()].map(([phrase, g]) => {
    const names = [...g.names].join("、");
    return g.count > 1 ? `${phrase}=${names}(${g.count} 张)` : `${phrase}=${names}`;
  });
  return `这镜锁定:${parts.join(" / ")}`;
}
